import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hasHeroImage, setHasHeroImage] = useState(false);
  const location = useLocation();

  // Check if page has hero image/video
  useEffect(() => {
    const checkHeroImage = () => {
      // Check for video element or image in first section
      const firstSection = document.querySelector('main > div > section:first-child, main section:first-child');
      if (firstSection) {
        const hasVideo = firstSection.querySelector('video');
        const hasImage = firstSection.querySelector('img');
        const hasBgImage = window.getComputedStyle(firstSection).backgroundImage !== 'none';
        const sectionClasses = firstSection.className || '';
        const isFullScreenHero = sectionClasses.includes('h-screen') || sectionClasses.includes('min-h-screen');
        
        setHasHeroImage(!!(hasVideo || (hasImage && isFullScreenHero) || hasBgImage));
      } else {
        setHasHeroImage(false);
      }
    };

    // Reset scroll position when route changes
    window.scrollTo(0, 0);
    
    // Check after a small delay to allow DOM to render
    setTimeout(checkHeroImage, 100);
    checkHeroImage();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';
  
  // Check if any dropdown item is active for a parent nav item
  const hasActiveDropdownItem = (item) => {
    if (!item.dropdown) return false;
    return item.dropdown.some(dropdownItem => isActive(dropdownItem.path));
  };

  // Nav text color: white when hero image or scrolled, dark otherwise
  const getTextColorClass = () => {
    if (isHomePage || hasHeroImage) return 'text-white';
    return isScrolled ? 'text-white' : 'text-white';
  };
  const getHoverColorClass = () => {
    if (isHomePage || hasHeroImage) return 'hover:text-gray-200';
    return isScrolled ? 'hover:text-gray-200' : 'hover:text-[#2a3890]';
  };

  const navItems = [
    {
      label: 'Our Company',
      path: '/',
      dropdown: [
        { path: '/financial-performance', label: 'Financial Performance' },
        { path: '/portfolio-summary', label: 'Portfolio Summary' },
        { path: '/leadership', label: 'Leadership' },
        { path: '/locations', label: 'Locations' },
        // { path: '/about-us', label: 'About Us'  },
      ]
    },
    {
      label: 'Our Services',
      path: '/core-services',
      dropdown: [
        { path: '/core-services', label: 'Core Services' },
        { path: '/about-us', label: 'About Us' },
      ]
    },
    { label: 'Portfolio', path: '/portfolio', dropdown: null },
    { label: 'Investments', path: '/news-insights', dropdown: null },
    { label: 'Contact', path: '/contact', dropdown: null },
  ];

  return (
    <header
      className={`w-full z-50 transition-all relative ${
        hasHeroImage || isHomePage
          ? (isScrolled ? 'bg-black/100' : 'bg-transparent')
          : (isScrolled ? 'bg-black/100' : 'bg-black/100')
      }`}
    >
      <nav className="container mx-auto px-3 mobile-normal:px-4 mobile-large:px-5 tablet-small:px-6 laptop-small:px-8" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="flex items-center justify-between h-14 mobile-normal:h-16 mobile-large:h-18 tablet-small:h-20">        
          {/* Logo - Left Side */}
          <Link to="/" className="flex flex-col items-start gap-1 group flex-shrink-0">
            <div className="relative top-2">
              <svg
                width="200"
                height="90"
                viewBox="0 0 600 150"
                fill="none"
                stroke="#2a3890"
                preserveAspectRatio="none"
                className="block drop-shadow w-[120px] h-[54px] mobile-normal:w-[140px] mobile-normal:h-[63px] mobile-large:w-[160px] mobile-large:h-[72px] tablet-small:w-[180px] tablet-small:h-[81px] tablet-standard:w-[200px] tablet-standard:h-[90px]"
                aria-hidden="true"
              >
                <path
                 strokeWidth="12" 
                 strokeLinecap="square"
                 strokeLinejoin="round" 
                 d="M 40 130 L 580 130 L 580 60 L 400 10 L 350 20 V 10 H 320 V 35  L 310 40 L 250 50" />
              </svg>
              <img
                src='/images/TM_Logo.png'
                alt="Company Logo"
                className="absolute left-1/2 -translate-x-1/2 bottom-4 h-6 w-auto mobile-normal:h-7 mobile-large:h-8 tablet-small:h-9 tablet-standard:h-10 drop-shadow-lg"
              />
            </div>
          </Link>

          {/* Desktop Navigation - Show only on laptop-standard and above */}
          <div className="hidden laptop-standard:flex items-center space-x-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.path}
                  onClick={() => {
                    // Scroll to top when navigating
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  // UPDATED: Classes for dynamic colors
                  className={`flex items-center px-3 laptop-standard:px-4 py-4 laptop-standard:py-6 font-semibold font-medium transition-colors duration-200 text-[14px] laptop-standard:text-[15px] ${
                    isActive(item.path) || hasActiveDropdownItem(item)
                      ? `border-b-2 border-[#2a3890] ${getTextColorClass()}`
                      : `${getTextColorClass()} ${getHoverColorClass()}`
                  }`}
                >
                  {item.label}
                  {item.dropdown && (
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                  )}
                </Link>

                {/* Dropdown Menu - Responsive sizing */}
                {item.dropdown && activeDropdown === item.label && (
                  <div className="absolute top-full left-0 bg-white shadow-lg border-t-2 border-[#2a3890] min-w-[200px] laptop-standard:min-w-[240px] py-1 laptop-standard:py-2 rounded-b-lg overflow-hidden">
                    {item.dropdown.map((dropdownItem) => (
                      <Link
                        key={dropdownItem.path}
                        to={dropdownItem.path}
                        className={`block px-4 laptop-standard:px-6 py-2 laptop-standard:py-3 text-xs laptop-standard:text-sm transition-colors ${
                          isActive(dropdownItem.path)
                            ? 'bg-blue-50 text-[#2a3890] font-semibold'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={() => {
                          setActiveDropdown(null);
                          // Handle hash links for same-page navigation
                          if (dropdownItem.path.includes('#')) {
                            const [path, hash] = dropdownItem.path.split('#');
                            setTimeout(() => {
                              const element = document.getElementById(hash);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            }, 100);
                          } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                      >
                        {dropdownItem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Facebook Social Icon */}
            <a 
              href="https://www.facebook.com/tminternationalconstruction/" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`ml-4 px-2 py-4 laptop-standard:py-6 transition-colors duration-200 ${getTextColorClass()} ${getHoverColorClass()}`}
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>

          {/* Mobile Menu Button - Always visible on mobile/tablet, white color for visibility */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="laptop-standard:hidden flex items-center justify-center focus:outline-none ml-2 mobile-normal:ml-3 mobile-large:ml-4 z-50 text-white hover:text-gray-200 transition-colors"
            aria-label="Toggle menu"
            style={{ 
              minWidth: '32px',
              minHeight: '32px',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <svg
              className="w-6 h-6 mobile-normal:w-7 mobile-normal:h-7"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation - Optimized for small screens */}
        {isMenuOpen && (
          <div className="laptop-standard:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg max-h-[85vh] overflow-y-auto z-40">
            <div className="py-2 mobile-normal:py-3 px-3 mobile-normal:px-4">
              {navItems.map((item) => (
                <div key={item.label} className="mb-1">
                  <div
                    onClick={() => {
                      if (item.dropdown) {
                        setActiveDropdown(activeDropdown === item.label ? null : item.label);
                      } else {
                        setIsMenuOpen(false);
                      }
                    }}
                    className={`block py-2.5 mobile-normal:py-3 text-sm mobile-normal:text-[15px] font-medium transition-colors duration-200 cursor-pointer rounded-md px-2 ${
                      isActive(item.path) || hasActiveDropdownItem(item)
                        ? 'text-[#2a3890] bg-blue-50'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {item.dropdown ? (
                      <div className="flex items-center justify-between">
                        <span>{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => {
                          setIsMenuOpen(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="block"
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                  {item.dropdown && activeDropdown === item.label && (
                    <div className="pl-3 mobile-normal:pl-4 mt-1 mb-2 border-l-2 border-[#2a3890] ml-2 mobile-normal:ml-3 space-y-1">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.path}
                          to={dropdownItem.path}
                          onClick={() => {
                            setIsMenuOpen(false);
                            // Handle hash links for same-page navigation
                            if (dropdownItem.path.includes('#')) {
                              const [path, hash] = dropdownItem.path.split('#');
                              setTimeout(() => {
                                const element = document.getElementById(hash);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }, 100);
                            } else {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }}
                          className={`block py-2 mobile-normal:py-2.5 text-xs mobile-normal:text-sm rounded-md px-2 transition-colors ${
                            isActive(dropdownItem.path)
                              ? 'bg-blue-50 text-[#2a3890] font-semibold'
                              : 'text-gray-600 hover:text-[#2a3890] hover:bg-blue-50'
                          }`}
                        >
                          {dropdownItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/contact"
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="block mt-3 mobile-normal:mt-4 px-4 py-2.5 mobile-normal:py-3 bg-[#2a3890] text-white text-center font-medium rounded-md hover:bg-[#1e2a6e] transition-colors text-sm mobile-normal:text-base"
              >
                Contact Us
              </Link>
              
              {/* Facebook Social Icon - Mobile */}
              <div className="flex justify-center mt-4 mobile-normal:mt-5">
                <a 
                  href="https://www.facebook.com/tminternationalconstruction/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-[#2a3890] transition-colors duration-200"
                  aria-label="Facebook"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;